import type { NostrEvent } from "@nostrify/nostrify";

/**
 * Check if an event is a direct reply to a specific event ID.
 * According to NIP-10:
 * - If markers are used, look for the 'e' tag with marker 'reply'
 * - If no markers, the last 'e' tag is the direct reply target
 */
export function isDirectReplyTo(
  event: NostrEvent,
  targetEventId: string,
): boolean {
  const eTags = event.tags.filter(([tag]) => tag === "e");
  if (eTags.length === 0) return false;

  // Check for marked reply tag first (NIP-10 preferred format)
  const markedReply = eTags.find((tag) => tag[3] === "reply");
  if (markedReply) {
    return markedReply[1] === targetEventId;
  }

  // Check for root marker - if the only e tag is the root, it's a direct reply
  const rootTag = eTags.find((tag) => tag[3] === "root");
  if (rootTag && eTags.length === 1) {
    return rootTag[1] === targetEventId;
  }

  // Fallback: no markers used, last 'e' tag is the reply target (deprecated NIP-10 format)
  const lastETag = eTags[eTags.length - 1];
  return lastETag[1] === targetEventId;
}

/**
 * Build NIP-10 compliant tags for a reply event.
 *
 * Always includes both a "root"-marked and a "reply"-marked "e" tag, even
 * when replying directly to the root (both tags then point to the same
 * event id). Many clients rely on the "root" tag to thread a reply - a
 * "reply" tag alone is not enough for it to show up in their thread view.
 */
export function buildReplyTags(
  rootEvent: NostrEvent,
  replyToEvent: NostrEvent,
): string[][] {
  const pubkeys = Array.from(
    new Set([rootEvent.pubkey, replyToEvent.pubkey]),
  );

  return [
    ["e", rootEvent.id, "", "root"],
    ["e", replyToEvent.id, "", "reply"],
    ...pubkeys.map((pubkey) => ["p", pubkey]),
  ];
}
